import { useEffect } from 'react'
import {
  BLOCK_PATTERNS,
  DEFAULT_LESSON_THEME,
  LESSON_THEMES,
  Label,
  patternCardLayer,
  resolvePatternId,
  type BlockPatternId,
  type LessonThemeId,
} from '@teach/ui'
import type { LessonUpdate } from '@/types/lesson'
import { lookNotes } from '@/features/editor/lib/lessonLook'
import { useAppDispatch } from '@/store/hooks'
import { setLessonLookPreview } from '@/features/editor/editorSlice'
import { useLessonLook } from './useLessonLook'
import { LookCardGroup } from './LookCardGroup'
import { useMessages } from '@/i18n'
import { messages } from './messages'

const NO_PATTERN = 'none'

// `RadioCardGroup.onChange` отдаёт сырой id: сужаем его таблицами, а не
// приведением типа — незнакомый id тогда просто ничего не выберет.
const asTheme = (id: string): LessonThemeId | null =>
  LESSON_THEMES.find((t) => t.id === id)?.id ?? null
const asPattern = (id: string): BlockPatternId | null =>
  id === NO_PATTERN ? null : resolvePatternId(id)

// Карточка — уменьшенный экран урока: геометрия узора та же, что у канваса,
// краска заметнее (`patternCardLayer`) — канвасная на мини-экране почти не видна.
const screenBackground = (theme: LessonThemeId, pattern: BlockPatternId | null): string => {
  const bg = (LESSON_THEMES.find((t) => t.id === theme) ?? DEFAULT_LESSON_THEME).background
  const layer = patternCardLayer(theme, pattern)
  return layer ? `${layer}, ${bg}` : bg
}

interface Props {
  courseId: number
  lessonId: number
  lessonTheme: string | null | undefined
  savePatch: (patch: LessonUpdate) => Promise<boolean>
  disabled: boolean
  /** Вкладка открыта: холст показывает экран с этим оформлением (`LessonLookPreview`). */
  previewOnCanvas?: boolean
}

/**
 * Оформление урока — ЕДИНСТВЕННОЕ место выбора темы и паттерна (в блоках
 * пикера больше нет). Тема уходит в `lesson.theme` и якорным хексом в блоки,
 * паттерн — только в блоки: поля-близнеца у урока нет.
 */
export const LessonLookSection = ({
  courseId,
  lessonId,
  lessonTheme,
  savePatch,
  disabled,
  previewOnCanvas = false,
}: Props) => {
  // `isBlocked` хука тут не читается намеренно: панель урока и редактор блока
  // взаимоисключающи (см. комментарий у поля в `useLessonLook`), грязного
  // черновика в этом месте не бывает — гейт был бы вечно ложным.
  const m = useMessages(messages)
  const { look, isLoaded, isSettling, apply } = useLessonLook(
    courseId,
    lessonId,
    lessonTheme,
    savePatch,
  )
  // Выбор идёт на холст сразу, не дожидаясь записи на сервер.
  const dispatch = useAppDispatch()
  const { theme, pattern } = look
  useEffect(() => {
    if (!previewOnCanvas) return
    dispatch(setLessonLookPreview({ theme, pattern }))
    return () => {
      dispatch(setLessonLookPreview(null))
    }
  }, [dispatch, previewOnCanvas, theme, pattern])
  // Пока блоки урока не приехали, выбирать нечего: применение всё равно не
  // уйдёт (см. `useLessonLook`), а живой контрол обещал бы обратное.
  // Переезд на черновик: рассылка собрала бы тела из живой версии.
  // Запись (`isBusy`) контролы НЕ гасит: выбор во время неё встаёт в очередь (`useLessonLook`).
  const locked = disabled || !isLoaded || isSettling
  const notes = lookNotes({ look, isLoaded, locked })
  const themeOptions = LESSON_THEMES.map((t) => ({
    id: t.id,
    label: t.label,
    background: screenBackground(t.id, look.pattern),
  }))
  const patternOptions = [
    { id: NO_PATTERN, label: m.noPattern },
    ...BLOCK_PATTERNS.map((p) => ({ id: p.id as string, label: p.label })),
  ].map((o) => ({
    ...o,
    background: screenBackground(look.theme ?? DEFAULT_LESSON_THEME.id, asPattern(o.id)),
  }))

  return (
    <section className="space-y-3">
      <Label className="text-foreground font-medium">{m.blocksBackground}</Label>
      <LookCardGroup
        options={themeOptions}
        value={look.theme}
        columns={3}
        ariaLabel={m.lessonTheme}
        disabled={locked}
        onChange={(id) => {
          const theme = asTheme(id)
          if (theme) void apply({ theme, pattern: look.pattern })
        }}
      />
      <Label className="block pt-2 text-foreground font-medium">{m.pattern}</Label>
      <LookCardGroup
        options={patternOptions}
        value={look.isMixedPattern ? null : (look.pattern ?? NO_PATTERN)}
        columns={4}
        ariaLabel={m.backgroundPattern}
        // Паттерн шлётся тем же телом, что и тема: без распознанной темы
        // рассылать нечего — сначала выбор темы.
        disabled={locked || look.theme === null}
        onChange={(id) => {
          if (look.theme) void apply({ theme: look.theme, pattern: asPattern(id) })
        }}
      />
      {notes.map((note) => (
        <p key={note} className="text-xs text-muted-foreground">
          {note}
        </p>
      ))}
    </section>
  )
}
